/**
 * Attribution capture (FR-REG-06): UTMs and click ids from the parent page URL, `_fbp`/`_fbc`
 * cookies when readable, referrer and landing URL. First-touch values are persisted in
 * sessionStorage so a visitor who navigates within the site before registering keeps them.
 */
import type { Attribution } from '@ffs/shared';

export const STORAGE_KEY = 'ffs:attribution';

const URL_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'ttclid',
] as const;

/** Max lengths mirror the `Attribution` schema so a long value never fails validation. */
const LIMITS: Record<keyof Attribution, number> = {
  utm_source: 200,
  utm_medium: 200,
  utm_campaign: 200,
  utm_content: 200,
  utm_term: 200,
  fbclid: 500,
  gclid: 500,
  ttclid: 500,
  fbp: 200,
  fbc: 500,
  referrer: 2000,
  landing_url: 2000,
};

export interface PageInput {
  search: string;
  cookie: string;
  referrer: string;
  href: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function parseCookies(cookie: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of cookie.split(';')) {
    const i = part.indexOf('=');
    if (i < 0) continue;
    const k = part.slice(0, i).trim();
    if (!k) continue;
    try {
      out[k] = decodeURIComponent(part.slice(i + 1).trim());
    } catch {
      out[k] = part.slice(i + 1).trim();
    }
  }
  return out;
}

function clip(key: keyof Attribution, value: string | null | undefined): string | undefined {
  const v = value?.trim();
  if (!v) return undefined;
  return v.length > LIMITS[key] ? v.slice(0, LIMITS[key]) : v;
}

/** Current-page attribution only (no persistence). */
export function attributionFromPage(page: PageInput): Attribution {
  const out: Attribution = {};
  const params = new URLSearchParams(page.search);
  for (const k of URL_KEYS) {
    const v = clip(k, params.get(k));
    if (v) out[k] = v;
  }
  const cookies = parseCookies(page.cookie);
  const fbp = clip('fbp', cookies._fbp);
  const fbc = clip('fbc', cookies._fbc);
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  const referrer = clip('referrer', page.referrer);
  if (referrer) out.referrer = referrer;
  const landing = clip('landing_url', page.href);
  if (landing) out.landing_url = landing;
  return out;
}

/**
 * Page attribution merged with the first-touch snapshot in storage. Stored (first-touch) values
 * win; the current page fills any gaps; the merged result is written back.
 */
export function captureAttribution(page: PageInput, storage?: StorageLike | null): Attribution {
  const current = attributionFromPage(page);
  if (!storage) return current;
  let stored: Attribution = {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      for (const k of Object.keys(LIMITS) as (keyof Attribution)[]) {
        const v = clip(k, typeof parsed[k] === 'string' ? (parsed[k] as string) : undefined);
        if (v) stored[k] = v;
      }
    }
  } catch {
    stored = {};
  }
  const merged: Attribution = { ...current, ...stored };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Storage may be unavailable (private mode, quota, sandboxed iframe); attribution still works.
  }
  return merged;
}

/** Browser entry point: reads the live document and sessionStorage. */
export function captureFromDocument(): Attribution {
  let storage: StorageLike | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return captureAttribution(
    {
      search: location.search,
      cookie: document.cookie,
      referrer: document.referrer,
      href: location.href,
    },
    storage,
  );
}
